/**
 * Konjugation back: the answer key, and where this verb keeps catching you.
 *
 * The Stammformen line is the reason this app does not show the principal
 * parts on the front. A weak participle contains the third person by
 * construction — `ge` + `arbeitet` — so printing `hat gearbeitet` above the
 * fields would hand over two of the eight answers on roughly half the deck,
 * and only on the regular verbs, which are the ones that needed no help. Here
 * it costs nothing: every part of the line was just answered, so it reads as
 * the key rather than a hint, coloured by how each part went.
 *
 * The miss bars are the payoff for `attempts.detail` (migration 0010). A
 * card-level boolean could only ever say "this verb keeps getting missed";
 * what a learner can act on is "`ihr` keeps getting missed".
 */
import { useQuery } from '@tanstack/react-query';
import { Check, MessageSquareQuote, TriangleAlert, X } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { Button } from '@/components/ui/button';
import {
  KONJUGATION_LABELS,
  type KonjugationDetail,
  type KonjugationField,
  type KonjugationPayload,
} from '@/core/types';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

import type { CardBackProps } from '../types';
import { fieldsFor, stammformen } from './fields';

type Props = CardBackProps<KonjugationPayload>;

/**
 * The fields that needed a retry this time, from the front's own report.
 *
 * Narrowed rather than cast: `detail` crosses the shell as `unknown`, and a
 * card answered before this app existed — or by a future version with a
 * different shape — must render as "nothing to highlight" rather than throw.
 */
function missedThisTime(detail: unknown): Set<string> {
  const fields = (detail as KonjugationDetail | undefined)?.fields;
  if (!Array.isArray(fields)) return new Set();
  // A form the user asked for was not produced either, so it is marked the same
  // way as one they got wrong.
  return new Set(fields.filter((f) => f && (f.tries > 0 || f.revealed)).map((f) => f.key));
}

export function CardBack({
  card,
  correct,
  answer_detail,
  secondary_lang,
  isLastCard,
  onNext,
}: Props) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  const fields = fieldsFor(card.payload);
  const missedNow = missedThisTime(answer_detail);

  const { data: stats } = useQuery({
    queryKey: ['card-field-stats', card.id],
    queryFn: () => api.cardFieldStats(card.id),
    staleTime: 0,
  });

  const firstTry = fields.length - missedNow.size;

  return (
    <div className="border-border bg-card flex flex-col gap-5 rounded-2xl border p-6 shadow-sm sm:p-8">
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2">
        <div>
          <h2 className="font-display text-2xl font-semibold sm:text-3xl">{card.de}</h2>
          <p className="text-muted-foreground text-sm">{(card.translations.en ?? []).join(', ')}</p>
        </div>
        <div
          className={cn(
            'inline-flex items-center gap-1.5 font-medium',
            correct ? 'text-success' : 'text-warning',
          )}
        >
          {correct ? <Check className="size-5" /> : <X className="size-5" />}
          <span className="font-mono text-sm tabular-nums">
            {firstTry} / {fields.length} first try
          </span>
        </div>
      </header>

      <section className="flex flex-col gap-2">
        <h3 className="text-muted-foreground text-xs font-bold tracking-wider uppercase">
          The full table
        </h3>
        <dl className="bg-muted/40 grid grid-cols-1 gap-x-6 gap-y-1 rounded-md p-3 sm:grid-cols-2">
          {fields.map((field) => (
            <div key={field.key} className="flex gap-2 font-mono text-sm">
              <dt className="text-muted-foreground min-w-[5.2rem]">{field.label}</dt>
              <dd className={cn('font-semibold', missedNow.has(field.key) && 'text-destructive')}>
                {field.answer}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <Stammformen card={card} missedNow={missedNow} />

      <MissHistory
        fields={fields.map((f) => f.key)}
        misses={stats?.misses ?? {}}
        attempts={stats?.attempts ?? 0}
      />

      {card.warning ? (
        <div className="bg-warning/10 flex items-start gap-2 rounded-md p-3 text-sm">
          <TriangleAlert className="text-warning mt-0.5 size-4 shrink-0" />
          <span>{card.warning}</span>
        </div>
      ) : null}

      {card.example ? (
        <div className="bg-muted/40 flex items-start gap-2 rounded-md p-3 text-sm">
          <MessageSquareQuote className="text-muted-foreground mt-0.5 size-4 shrink-0" />
          <div>
            <div>{card.example}</div>
            {card.example_translations[secondary_lang] ? (
              <div className="text-muted-foreground mt-1 italic">
                ({card.example_translations[secondary_lang]})
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <Button ref={buttonRef} size="lg" onClick={onNext} className="w-full">
        {isLastCard ? 'Finish session' : 'Next verb →'}
      </Button>
    </div>
  );
}

/** The dictionary line: Infinitiv · Präsens · Präteritum · Perfekt. */
function Stammformen({ card, missedNow }: { card: Props['card']; missedNow: Set<string> }) {
  const parts = stammformen(card.de, card.payload);
  // Index 0 is the infinitive — the prompt, not an answer, so it stays neutral.
  const keyed: (KonjugationField | null)[] = [null, 'er', 'praeteritum', 'perfekt'];

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-muted-foreground text-xs font-bold tracking-wider uppercase">
        Stammformen
      </h3>
      <div className="bg-muted/40 rounded-md p-3">
        <p className="flex flex-wrap items-baseline font-mono text-sm font-semibold">
          {parts.map((part, i) => {
            const key = keyed[i];
            const missed = key ? missedNow.has(key) : false;
            return (
              <span key={part}>
                {i > 0 ? <span className="text-muted-foreground px-2">·</span> : null}
                <span className={cn(missed ? 'text-warning' : key ? 'text-success' : '')}>
                  {part}
                </span>
              </span>
            );
          })}
        </p>
        <p className="text-muted-foreground mt-1.5 text-xs">
          Infinitiv · Präsens · Präteritum · Perfekt
        </p>
      </div>
    </section>
  );
}

/**
 * Lifetime misses per field.
 *
 * Hidden entirely on a card with no history rather than shown as six empty
 * tracks: a chart of nothing is worse than no chart, and the first answer to a
 * card is exactly when there is nothing to say.
 */
function MissHistory({
  fields,
  misses,
  attempts,
}: {
  fields: KonjugationField[];
  misses: Partial<Record<KonjugationField, number>>;
  attempts: number;
}) {
  const counts = fields.map((key) => misses[key] ?? 0);
  const worst = Math.max(0, ...counts);
  if (attempts === 0 || worst === 0) return null;

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-muted-foreground text-xs font-bold tracking-wider uppercase">
        Where this verb catches you · {attempts} {attempts === 1 ? 'time' : 'times'} seen
      </h3>
      <div className="flex flex-col gap-1">
        {fields.map((key, i) => {
          const count = counts[i];
          const isWorst = count === worst;
          return (
            <div key={key} className="grid grid-cols-[5.5rem_1fr_2rem] items-center gap-3 text-sm">
              <span className="text-muted-foreground text-right font-mono text-xs">
                {KONJUGATION_LABELS[key]}
              </span>
              <span className="bg-muted h-2 overflow-hidden rounded-full">
                <span
                  className={cn(
                    'block h-full rounded-full',
                    isWorst ? 'bg-destructive/75' : 'bg-primary/45',
                  )}
                  style={{ width: `${(count / worst) * 100}%` }}
                />
              </span>
              <span
                className={cn(
                  'text-right font-mono text-xs tabular-nums',
                  isWorst ? 'text-destructive font-bold' : 'text-muted-foreground',
                )}
              >
                {count}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
